// /profile/connections (yours) and /profile/:user_id/connections — following,
// followers, suggestions and (yours only) blocked people. An account page
// (pages/account/kit.js); in the app it's the native Connections screen, whose
// back closes it ("connectionNavigationBack").
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { useTranslation } from "react-i18next";
import { validate as isValidUUID } from "uuid";
import { Ban, BookUser, EyeOff, MessageCircle, MoreHorizontal, Search, Share2, ShieldCheck, Users, X } from "lucide-react";
import authAxios from "../../authAxios";
import instance from "../../axios";
import { useIsMobile } from "../../hooks/responsive";
import { useGlobalState } from "../../GlobalStates";
import { getDisplayName, getFullName } from "../../utils/userDisplayName";
import { openNativeProfile } from "../../utils/nativeProfileBridge";
import UserProfileBottomSheet from "./components/UserProfileBottomSheet";
import { ReportUserDialog } from "./components/ReportUserDialog";
import { BlockUserConfirmationModal, UnblockUserConfirmationModal } from "./components/BlockConfirmationModals";
import { Button, ConfirmModal, Dialog, EmptyState, MenuItem, Skeleton, cn } from "../../pages/manage/ui";
import { clubToast } from "../../pages/manage/toast";
import { AccountPage, isDedicatedAppScreen, postToApp } from "../../pages/account/kit";

const Connections = () => {
  const [user] = useGlobalState("user");
  const [userLoading] = useGlobalState("userLoading");

  if (userLoading) {
    return (
      <AccountPage bar={false} hideTitle>
        <RowSkeletons />
      </AccountPage>
    );
  }

  if (!user.id) {
    return <Navigate to="/" />;
  }

  return <ConnectionsPage />;
};

const RowSkeletons = () => (
  <div className="tw-flex tw-flex-col">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="tw-flex tw-items-center tw-gap-3 tw-py-3">
        <Skeleton className="tw-size-11 tw-shrink-0 tw-rounded-full" />
        <div className="tw-flex-1">
          <Skeleton className="tw-h-4 tw-w-32" />
          <Skeleton className="tw-mt-2 tw-h-3 tw-w-20" />
        </div>
        <Skeleton className="tw-h-[30px] tw-w-20 tw-rounded-[var(--r-xl)]" />
      </div>
    ))}
  </div>
);

const Avatar = ({ src, name, dim = false, size = "tw-size-11" }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className={cn("tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-[var(--fill-strong)] tw-text-[15px] tw-font-semibold tw-text-muted-foreground", size)}>
      {src && !failed ? (
        <img src={src} alt={name} onError={() => setFailed(true)} className={cn("tw-size-full tw-object-cover", dim && "tw-opacity-60")} />
      ) : (
        name?.charAt(0)?.toUpperCase() || "?"
      )}
    </span>
  );
};

// One person in a list: avatar, name, the follow action, and (on your own
// lists) the "…" menu or, for suggestions, a dismiss button.
const UserRow = ({ person, mode, followingHash, followersHash, canManage, onFollowAction, onMenu, onDismiss }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [showUserProfile, setShowUserProfile] = useState(false);
  const { id, avatar, username, mutual_connections, recommendation_type } = person;
  const name = getDisplayName(person) || getFullName(person);

  const isFollowing = followingHash && followingHash.is_approved;
  const isPendingApproval = followingHash && !followingHash.is_approved;
  const isPrivateUser = mode === "followers" ? false : followersHash?.is_private;
  // Someone who asked to follow us and is waiting for our approval.
  const isFollowerPendingOurApproval = mode === "followers" && followersHash && !followersHash.is_approved;

  const action = isFollowerPendingOurApproval
    ? { label: t("accept_request", { defaultValue: "Accept request" }), primary: true }
    : isPendingApproval
      ? isMobile
        ? { label: t("cancel_request", { defaultValue: "Cancel request" }) }
        : { label: t("requested", { defaultValue: "Requested" }), hover: t("cancel_request", { defaultValue: "Cancel request" }) }
      : isFollowing
        ? isMobile
          ? { label: t("unfollow", { defaultValue: "Unfollow" }) }
          : { label: t("following", { defaultValue: "Following" }), hover: t("unfollow", { defaultValue: "Unfollow" }) }
        : mode === "followers" && !followingHash
          ? { label: isPrivateUser ? t("request_follow", { defaultValue: "Request follow" }) : t("follow_back", { defaultValue: "Follow back" }), primary: true }
          : { label: t("follow", { defaultValue: "Follow" }), primary: true };

  const stop = (fn) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    fn();
  };

  // In the app a profile opens as the native profile screen; phones on the web
  // slide it in as a sheet; wider screens follow the link.
  const handleRowClick = (e) => {
    if (openNativeProfile(id)) {
      e?.preventDefault?.();
      return;
    }
    if (isMobile) setShowUserProfile(true);
  };

  const body = (
    <div className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-[var(--hairline)] tw-py-3">
      <Avatar src={avatar} name={name} />
      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-truncate tw-text-[15px] tw-font-semibold">{name}</p>
        {username && <p className="tw-truncate tw-text-[13px] tw-text-muted-foreground">{username}</p>}
        {mode === "recommendations" && recommendation_type === 1 && mutual_connections?.length > 0 && (
          <div className="tw-mt-1 tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-flex tw--space-x-1.5">
              {mutual_connections.slice(0, 3).map((connection) => (
                <img key={connection.id} src={connection.image} alt="" className="tw-size-5 tw-rounded-full tw-object-cover tw-ring-2 tw-ring-background" />
              ))}
            </span>
            <span className="tw-truncate tw-text-[13px] tw-text-muted-foreground">
              {mutual_connections.length === 1
                ? t("followed_by_single", { defaultValue: `Followed by ${getDisplayName(mutual_connections[0])}`, first_name: getDisplayName(mutual_connections[0]) })
                : t("followed_by_multiple", {
                    defaultValue: `Followed by ${getDisplayName(mutual_connections[0])} & ${mutual_connections.length - 1} ${mutual_connections.length - 1 === 1 ? "other" : "others"}`,
                    first_name: getDisplayName(mutual_connections[0]),
                    count: mutual_connections.length - 1,
                  })}
            </span>
          </div>
        )}
        {mode === "recommendations" && recommendation_type === "1.5" && (
          <p className="tw-mt-1 tw-text-[13px] tw-text-muted-foreground">{t("was_on_the_same_trip_as_you", { defaultValue: "Was on the same trip as you!" })}</p>
        )}
      </div>
      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
        <Button
          size="sm"
          variant={action.primary ? "default" : "light"}
          onClick={stop(() => onFollowAction(person, { isFollowerPendingOurApproval, isFollowing, isPendingApproval, isPrivateUser, mode }))}
          className="tw-group/follow"
        >
          {action.hover ? (
            <>
              <span className="group-hover/follow:tw-hidden">{action.label}</span>
              <span className="tw-hidden group-hover/follow:tw-inline">{action.hover}</span>
            </>
          ) : (
            action.label
          )}
        </Button>
        {canManage && (
          <Button variant="ghost" size="icon-sm" className="tw-rounded-full tw-text-muted-foreground" aria-label={t("more", { defaultValue: "More" })} onClick={stop(() => onMenu(person))}>
            <MoreHorizontal />
          </Button>
        )}
        {mode === "recommendations" && (
          <Button variant="ghost" size="icon-sm" className="tw-rounded-full tw-text-muted-foreground" aria-label={t("remove_suggestion", { defaultValue: "Remove suggestion" })} onClick={stop(() => onDismiss(person))}>
            <X />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? (
        <div role="button" tabIndex={0} onClick={handleRowClick} className="tw-cursor-pointer">
          {body}
        </div>
      ) : (
        <Link to={`/profile/${id}`} onClick={handleRowClick} className="tw-block tw-rounded-[var(--r-md)] tw-transition-colors hover:tw-bg-[var(--fill)]">
          {body}
        </Link>
      )}
      <UserProfileBottomSheet showUserProfile={showUserProfile} setShowUserProfile={setShowUserProfile} userId={id} />
    </>
  );
};

const ConnectionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user_id: _user_id } = useParams();
  const [user] = useGlobalState("user");

  const validParamUserId = _user_id && isValidUUID(_user_id) ? _user_id : null;
  const isOtherUserProfile = !!validParamUserId;
  const user_id = isOtherUserProfile ? validParamUserId : user.id;
  const currentUserId = user.id;

  const [activeTab, setActiveTab] = useState("following");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  // Recommendations is fetched on mount, so start as loading=true to render
  // skeletons on the very first paint instead of the empty state. Without
  // this the user sees a flash of "You haven't followed anyone yet" before
  // the fetch resolves — most visible after back-navigating into Connections
  // (e.g. returning from the native profile screen).
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [menuUser, setMenuUser] = useState(null);
  const [hideActivityUser, setHideActivityUser] = useState(null);
  const [userToRemove, setUserToRemove] = useState(null);
  const [userToBlock, setUserToBlock] = useState(null);
  const [isBlocking, setIsBlocking] = useState(false);
  const [userToUnblock, setUserToUnblock] = useState(null);
  const [isUnblocking, setIsUnblocking] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);

  const [following, setFollowing] = useState([]);
  const [authFollowing, setAuthFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [authFollowers, setAuthFollowers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);

  // Initialize loading=true so the very first render goes straight to the
  // skeleton state instead of briefly flashing the empty / "no users yet"
  // copy. The mount-time useEffect kicks the fetch right after, but the
  // first paint happens before that effect runs.
  const [loadingFollowers, setLoadingFollowers] = useState(true);
  const [loadingFollowing, setLoadingFollowing] = useState(true);
  const [loadingBlocked, setLoadingBlocked] = useState(true);

  const inAppScreen = isDedicatedAppScreen();

  useEffect(() => {
    // Listen for contactsImported event from React Native
    const handleMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.event === "contactsImported") {
          const { emails, phoneNumbers } = message.data;
          navigate("/profile/connections/import-contacts", {
            state: { emails, phoneNumbers },
          });
        }
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate]);

  const handleImportContacts = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("importContacts");
    } else {
      console.error("ReactNativeWebView bridge is not available");
    }
  };

  const shownFollowing = following.filter((f) => f.is_approved);
  const followersCount = followers.filter((f) => f.is_approved).length;

  const followersHash = authFollowers.reduce((acc, u) => {
    acc[u.follower_id] = {
      is_private: u.follower_is_private,
      hide_activity: u.hide_activity,
      is_approved: u.is_approved,
    };
    return acc;
  }, {});

  const followingHash = authFollowing.reduce((acc, u) => {
    acc[u.following_id] = {
      is_approved: u.is_approved,
    };
    return acc;
  }, {});

  async function getFollowing() {
    setLoadingFollowing(true);
    try {
      const { data } = await instance.get("/v1/social/getFollowing", { params: { user_id } });
      setFollowing(data);
    } catch (error) {
      console.error("Failed to fetch following:", error);
    }
    try {
      const { data } = await instance.get("/v1/social/getFollowing", { params: { user_id: currentUserId } });
      setAuthFollowing(data);
    } catch (error) {
      console.error("Failed to fetch auth following:", error);
    }
    setLoadingFollowing(false);
  }

  async function getFollowers() {
    setLoadingFollowers(true);
    try {
      const { data } = await instance.get("/v1/social/getFollowers", { params: { user_id } });
      setFollowers(data);
    } catch (error) {
      console.error("Failed to fetch followers:", error);
    }
    try {
      const { data } = await instance.get("/v1/social/getFollowers", { params: { user_id: currentUserId } });
      setAuthFollowers(data);
    } catch (error) {
      console.error("Failed to fetch auth followers:", error);
    }
    setLoadingFollowers(false);
  }

  async function getRecommendations() {
    setLoadingRecommendations(true);
    try {
      const { data } = await authAxios.get("/v1/social/getConnectionRecommendations");
      setRecommendations(data);
    } catch (error) {
      console.error("Failed to fetch recommendations:", error);
    }
    setLoadingRecommendations(false);
  }

  async function getBlockedUsers() {
    setLoadingBlocked(true);
    try {
      const { data } = await authAxios.get("/v1/social/getBlockedUsers");
      setBlockedUsers(data.data || []);
    } catch (error) {
      console.error("Failed to fetch blocked users:", error);
    }
    setLoadingBlocked(false);
  }

  async function searchUsers(searchTerm) {
    setLoadingSearch(true);
    try {
      const { data } = await instance.get(`/v1/social/searchSocials?search=${searchTerm}`);
      setSearchResults(data);
    } catch (error) {
      console.error("Failed to search users:", error);
    }
    setLoadingSearch(false);
  }

  useEffect(() => {
    getFollowing();
    getFollowers();
    getRecommendations();
    if (!isOtherUserProfile) {
      getBlockedUsers();
    } else {
      setLoadingBlocked(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    _.debounce((value) => {
      if (value) {
        searchUsers(value);
        setDebouncedSearchQuery(value);
      } else {
        setSearchResults([]);
        setDebouncedSearchQuery("");
      }
    }, 500),
    []
  );

  const loading = loadingFollowers || loadingFollowing || loadingRecommendations || loadingSearch || loadingBlocked;

  // ------------------------------------------------------------ actions

  const handleFollowAction = async (person, { isFollowerPendingOurApproval, isFollowing, isPendingApproval, isPrivateUser, mode }) => {
    const { id } = person;
    try {
      // Accept a follower request
      if (isFollowerPendingOurApproval) {
        setAuthFollowers((prev) => prev.map((f) => (f.follower_id === id ? { ...f, is_approved: true } : f)));
        await authAxios.post(`/v1/social/acceptFollowRequest/${id}`);
        return;
      }
      if (isFollowing) {
        setAuthFollowing((prev) => prev.filter((f) => f.following_id !== id));
        await authAxios.post(`/v1/social/unfollowUser/${id}`);
        return;
      }
      if (isPendingApproval) {
        setAuthFollowing((prev) => prev.filter((f) => f.following_id !== id));
        await authAxios.post(`/v1/social/cancelFollowRequest/${id}`);
        return;
      }
      const newFollowing = {
        following_id: id,
        following_first_name: person.first_name,
        following_last_name: person.last_name,
        following_username: person.username,
        following_image: person.avatar,
        is_approved: !isPrivateUser,
      };
      if (mode === "recommendations") {
        setRecommendations((prev) => prev.filter((rec) => rec.id !== id));
      }
      setAuthFollowing((prev) => [...prev, newFollowing]);
      await authAxios.post(`/v1/social/followUser/${id}`);
    } catch (error) {
      console.error("Failed to update follow status:", error);
      if (mode === "following") getFollowing();
      else if (mode === "followers") getFollowers();
      else if (mode === "recommendations") getRecommendations();
    }
  };

  const confirmRemoveSuggestion = async () => {
    if (!userToRemove) return;
    const removed = userToRemove;
    const originalRecommendations = [...recommendations];
    setRecommendations((prev) => prev.filter((rec) => rec.id !== removed.id));
    setUserToRemove(null);
    try {
      await authAxios.post(`/v1/social/blockRecommendation/${removed.id}`);
      clubToast.success(t("suggestion_removed_success", { username: removed.username }));
    } catch (error) {
      console.error("Failed to remove suggestion:", error);
      setRecommendations(originalRecommendations);
      clubToast.error(t("suggestion_removed_error", { defaultValue: "Failed to remove suggestion. Please try again." }));
    }
  };

  const toggleActivity = async (person) => {
    const isHidden = followersHash[person.id]?.hide_activity;
    const previous = authFollowers;
    setAuthFollowers((prev) => prev.map((f) => (f.follower_id === person.id ? { ...f, hide_activity: !isHidden } : f)));
    try {
      if (!isHidden) await authAxios.post(`/v1/social/hideActivity1on1/${person.id}`);
      else await authAxios.post(`/v1/social/showActivity1on1/${person.id}`);
    } catch (err) {
      console.error("Error toggling activity visibility:", err);
      setAuthFollowers(previous);
    }
  };

  const handleBlockUser = async () => {
    if (!userToBlock) return;
    const userName = userToBlock.username || getDisplayName(userToBlock) || getFullName(userToBlock);
    const reportUserId = userToBlock.id;
    try {
      setIsBlocking(true);
      await authAxios.post(`/v1/social/blockUser/${userToBlock.id}`);

      if (activeTab === "followers") {
        setAuthFollowers((prev) => prev.filter((f) => f.follower_id !== userToBlock.id));
      } else if (activeTab === "following") {
        setAuthFollowing((prev) => prev.filter((f) => f.following_id !== userToBlock.id));
      }
      setBlockedUsers((prev) => [
        ...prev,
        {
          id: userToBlock.id,
          first_name: userToBlock.first_name,
          last_name: userToBlock.last_name,
          display_name: userToBlock.display_name,
          username: userToBlock.username,
          image: userToBlock.avatar || userToBlock.image,
          blocked_at: new Date().toISOString(),
        },
      ]);
      clubToast.success(t("user_blocked_success", { defaultValue: `${userName} has been blocked`, name: userName }));
      setUserToBlock(null);

      // Report right after the block (the report dialog asks for a reason).
      setTimeout(() => setReportTarget({ userId: reportUserId, userName }), 150);
    } catch (error) {
      console.error("Error blocking user:", error);
      clubToast.error(t("user_blocked_error", { defaultValue: "Failed to block user. Please try again." }));
    } finally {
      setIsBlocking(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!userToUnblock) return;
    try {
      setIsUnblocking(true);
      await authAxios.post(`/v1/social/unblockUser/${userToUnblock.id}`);
      setBlockedUsers((prev) => prev.filter((u) => u.id !== userToUnblock.id));
      clubToast.success(t("user_unblocked_success", { defaultValue: `${userToUnblock.name} has been unblocked`, name: userToUnblock.name }));
      setUserToUnblock(null);
    } catch (error) {
      console.error("Error unblocking user:", error);
      clubToast.error(t("user_unblocked_error", { defaultValue: "Failed to unblock user. Please try again." }));
    } finally {
      setIsUnblocking(false);
    }
  };

  const shareProfile = async (person) => {
    const name = person ? getDisplayName(person) || getFullName(person) : getDisplayName(user) || getFullName(user);
    const url = `${window.location.origin}/profile/${person ? person.id : user.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: name, text: t("share_profile_text", { name }), url });
      } else {
        await navigator.clipboard.writeText(url);
        clubToast.success(t("profile_link_copied", { defaultValue: "Profile link copied" }));
      }
    } catch (err) {
      console.log("Error sharing:", err);
    }
  };

  // -------------------------------------------------------------- views

  const rowProps = (mode) => ({
    mode,
    canManage: !isOtherUserProfile,
    onFollowAction: handleFollowAction,
    onMenu: setMenuUser,
    onDismiss: setUserToRemove,
  });

  const list = (items, mode, toPerson) => (
    <Virtuoso
      useWindowScroll
      totalCount={items.length}
      itemContent={(index) => {
        const person = toPerson(items[index]);
        return <UserRow key={person.id} person={person} followingHash={followingHash[person.id]} followersHash={followersHash[person.id]} {...rowProps(mode)} />;
      }}
    />
  );

  const empty = (icon, title, action) => (
    <div className="tw-mt-2 tw-rounded-[var(--r-card)] tw-bg-[var(--fill)]">
      <EmptyState compact icon={icon} title={title} action={action} />
    </div>
  );

  const TABS = [
    { key: "following", label: t("following", { defaultValue: "Following" }), count: !loadingFollowing ? shownFollowing.length : null, fetch: getFollowing },
    { key: "followers", label: t("followers", { defaultValue: "Followers" }), count: followersCount || null, fetch: getFollowers },
    { key: "recommendations", label: t("suggested", { defaultValue: "Suggested" }), fetch: getRecommendations },
    !isOtherUserProfile && { key: "blocked", label: t("blocked", { defaultValue: "Blocked" }), count: blockedUsers.length || null, fetch: getBlockedUsers },
  ].filter(Boolean);

  const menuName = menuUser ? getDisplayName(menuUser) || getFullName(menuUser) : "";
  const menuIsBlocked = menuUser && blockedUsers.some((b) => b.id === menuUser.id);
  const menuCanHide = menuUser && activeTab === "followers" && followingHash[menuUser.id]?.is_approved;

  return (
    <AccountPage
      title={t("connections", { defaultValue: "Connections" })}
      description={t("manage_your_network_of_followers_and_people_you_follow", { defaultValue: "Manage your network of followers and people you follow" })}
      back={{ fallback: isOtherUserProfile ? `/profile/${user_id}` : "/profile", appMessage: "connectionNavigationBack" }}
    >
      {/* Search */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-relative tw-flex-1">
          <Search className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-size-4 tw--translate-y-1/2 tw-text-muted-foreground" />
          <input
            type="search"
            placeholder={t("search_all_users", { defaultValue: "Search all users" })}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              debouncedSearch(e.target.value);
            }}
            className="tw-h-11 tw-w-full tw-rounded-[var(--r-md)] tw-bg-[var(--fill)] tw-pl-10 tw-pr-3.5 tw-text-base tw-transition-[background,box-shadow] placeholder:tw-text-muted-foreground/70 hover:tw-bg-[var(--fill-strong)] focus:tw-bg-background focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring/40"
          />
        </div>
        {inAppScreen && !isOtherUserProfile && (
          <Button variant="light" size="icon" className="tw-size-11" aria-label={t("account_import_contacts", { defaultValue: "Find friends from your contacts" })} onClick={handleImportContacts}>
            <BookUser className="tw-size-5" />
          </Button>
        )}
      </div>

      {/* Tabs (hidden while searching) */}
      {!debouncedSearchQuery && (
        <div className="tw-sticky tw-top-14 tw-z-20 tw--mx-4 tw-mt-4 tw-bg-background tw-px-4 sm:tw-top-[86px] md:tw--mx-6 md:tw-px-6">
          <nav className="cs-tabbar" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  tab.fetch();
                }}
                className="cs-tab tw-shrink-0"
              >
                {tab.label}
                {tab.count != null && <span className="tw-ml-1.5 tw-text-muted-foreground">{tab.count}</span>}
              </button>
            ))}
          </nav>
        </div>
      )}

      <div key={debouncedSearchQuery ? "search" : activeTab} className="tw-mt-1">
        {loading ? (
          <RowSkeletons />
        ) : debouncedSearchQuery ? (
          searchResults.length > 0 ? (
            list(searchResults, "search", (r) => ({ ...r, avatar: r.image }))
          ) : (
            empty(Search, t("no_users_found_matching_your_search", { defaultValue: "No users found matching your search" }))
          )
        ) : activeTab === "following" ? (
          shownFollowing.length > 0 ? (
            list(shownFollowing, "following", (f) => ({
              id: f.following_id,
              created_at: f.created_at,
              avatar: f.following_image,
              first_name: f.following_first_name,
              last_name: f.following_last_name,
              display_name: f.following_display_name || f.following_first_name,
              username: f.following_username,
            }))
          ) : (
            empty(
              Users,
              t("you_are_not_following_anyone_yet", { defaultValue: "You are not following anyone yet" }),
              <Button variant="light" onClick={() => setActiveTab("recommendations")}>
                {t("discover_people_to_follow", { defaultValue: "Discover people to follow" })}
              </Button>
            )
          )
        ) : activeTab === "followers" ? (
          followers.length > 0 ? (
            list(followers, "followers", (f) => ({
              id: f.follower_id,
              created_at: f.created_at,
              avatar: f.follower_image,
              first_name: f.follower_first_name,
              last_name: f.follower_last_name,
              display_name: f.follower_display_name || f.follower_first_name,
              username: f.follower_username,
            }))
          ) : (
            empty(
              Users,
              t("you_don_t_have_any_followers_yet", { defaultValue: "You don't have any followers yet" }),
              !isOtherUserProfile && (
                <Button variant="light" onClick={() => shareProfile(null)}>
                  <Share2 />
                  {t("share_your_profile_to_get_followers", { defaultValue: "Share your profile to get followers" })}
                </Button>
              )
            )
          )
        ) : activeTab === "recommendations" ? (
          recommendations.length > 0 ? (
            list(recommendations, "recommendations", (r) => ({ ...r, avatar: r.image }))
          ) : (
            empty(Users, t("no_recommendations_available_at_the_moment", { defaultValue: "No recommendations available at the moment" }))
          )
        ) : blockedUsers.length > 0 ? (
          <Virtuoso
            useWindowScroll
            totalCount={blockedUsers.length}
            itemContent={(index) => {
              const blockedUser = blockedUsers[index];
              const name = getDisplayName(blockedUser) || getFullName(blockedUser);
              return (
                <div className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-[var(--hairline)] tw-py-3">
                  <Avatar src={blockedUser.image} name={name} dim />
                  <div className="tw-min-w-0 tw-flex-1">
                    <p className="tw-truncate tw-text-[15px] tw-font-semibold">{name}</p>
                    <p className="tw-truncate tw-text-[13px] tw-text-muted-foreground">
                      {[blockedUser.username, blockedUser.blocked_at && `${t("blocked_on", { defaultValue: "Blocked on" })} ${new Date(blockedUser.blocked_at).toLocaleDateString()}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <Button variant="light" size="sm" onClick={() => setUserToUnblock({ id: blockedUser.id, name })}>
                    {t("unblock", { defaultValue: "Unblock" })}
                  </Button>
                </div>
              );
            }}
          />
        ) : (
          empty(ShieldCheck, t("no_blocked_users", { defaultValue: "No blocked users" }))
        )}
      </div>

      {/* "…" on a person in your lists */}
      <Dialog open={!!menuUser} onClose={() => setMenuUser(null)} title={menuName} description={menuUser?.username}>
        {menuUser && (
          <div className="tw--mx-2.5 tw--my-1.5" role="menu">
            <MenuItem
              icon={Share2}
              label={t("share_profile", { defaultValue: "Share profile" })}
              onClick={() => {
                const person = menuUser;
                setMenuUser(null);
                shareProfile(person);
              }}
            />
            {window.isInApp && (
              <MenuItem
                icon={MessageCircle}
                label={t("account_profile_message", { defaultValue: "Message" })}
                onClick={() => {
                  postToApp(JSON.stringify({ type: "navigateToDirectMessage", userId: menuUser.id }));
                  setMenuUser(null);
                }}
              />
            )}
            {menuCanHide && (
              <MenuItem
                icon={EyeOff}
                label={followersHash[menuUser.id]?.hide_activity ? t("show_activity", { defaultValue: "Show activity" }) : t("hide_activity", { defaultValue: "Hide activity" })}
                onClick={() => {
                  const person = menuUser;
                  setMenuUser(null);
                  if (followersHash[person.id]?.hide_activity) toggleActivity(person);
                  else setHideActivityUser(person);
                }}
              />
            )}
            <div className="tw-mx-1 tw-my-1 tw-h-px tw-bg-[var(--hairline)]" />
            {menuIsBlocked ? (
              <MenuItem
                icon={ShieldCheck}
                label={t("unblock_user", { defaultValue: "Unblock user" })}
                onClick={() => {
                  setUserToUnblock({ id: menuUser.id, name: menuName });
                  setMenuUser(null);
                }}
              />
            ) : (
              <MenuItem
                icon={Ban}
                danger
                label={t("block_user", { defaultValue: "Block user" })}
                onClick={() => {
                  setUserToBlock(menuUser);
                  setMenuUser(null);
                }}
              />
            )}
          </div>
        )}
      </Dialog>

      <ConfirmModal
        open={!!hideActivityUser}
        onClose={() => setHideActivityUser(null)}
        onConfirm={() => {
          const person = hideActivityUser;
          setHideActivityUser(null);
          toggleActivity(person);
        }}
        title={t("hide_activity_from_follower", { defaultValue: "Hide activity from follower?" })}
        message={t("hide_activity_from_follower_text", {
          defaultValue: `We won't tell ${hideActivityUser?.username} that they were removed from your activity updates`,
          user: hideActivityUser?.username,
        })}
        confirmLabel={t("hide_activity", { defaultValue: "Hide activity" })}
      />

      <ConfirmModal
        open={!!userToRemove}
        onClose={() => setUserToRemove(null)}
        onConfirm={confirmRemoveSuggestion}
        danger
        title={t("account_remove_suggestion", { defaultValue: "Remove suggestion?" })}
        message={t("are_you_sure_you_want_to_remove", {
          defaultValue: `Are you sure you want to remove ${getDisplayName(userToRemove)} from your recommendations? You won't see them suggested again.`,
          userToRemove: getDisplayName(userToRemove),
        })}
        confirmLabel={t("remove", { defaultValue: "Remove" })}
      />

      <BlockUserConfirmationModal
        open={!!userToBlock}
        onClose={() => setUserToBlock(null)}
        userName={userToBlock ? userToBlock.username || getDisplayName(userToBlock) || getFullName(userToBlock) : ""}
        onConfirm={handleBlockUser}
        isBlocking={isBlocking}
      />

      <ReportUserDialog
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        userId={reportTarget?.userId}
        userName={reportTarget?.userName || ""}
        onSuccess={(message) => clubToast.success(message)}
      />

      <UnblockUserConfirmationModal
        open={!!userToUnblock}
        onClose={() => setUserToUnblock(null)}
        userName={userToUnblock ? userToUnblock.name : ""}
        onConfirm={handleUnblockUser}
        isUnblocking={isUnblocking}
      />
    </AccountPage>
  );
};

export default Connections;
